/**
 * CaseTrackUS - Reset Password Page
 */

import React, { useState } from 'react';
import { Container, Box, TextField, Button, Typography, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://casetrackus.com';

export default function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleReset = async () => {
    if (!password || password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/auth/reset-password?token=${token}&new_password=${encodeURIComponent(password)}`);
      setDone(true);
      setMsg('✅ Password reset successfully! You can now sign in with your new password.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired reset link. Please request a new one from the login page.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="xs">
        <Box sx={{ background: 'white', borderRadius: 2, p: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1B5E20', textAlign: 'center' }}>
            🔑 Reset Password
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 3, textAlign: 'center' }}>
            Enter your new password below.
          </Typography>

          {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!done ? (
            <>
              <TextField fullWidth label="New Password" type={showPassword ? 'text' : 'password'}
                value={password} onChange={e => setPassword(e.target.value)} sx={{ mb: 2 }}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}} />
              <TextField fullWidth label="Confirm Password" type={showConfirm ? 'text' : 'password'}
                value={confirm} onChange={e => setConfirm(e.target.value)} sx={{ mb: 3 }}
                onKeyPress={e => e.key === 'Enter' && handleReset()}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}} />
              <Button fullWidth variant="contained" size="large" onClick={handleReset} disabled={loading}
                sx={{ background: '#2E7D32', py: 1.5, borderRadius: 2 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
              </Button>
              <Button fullWidth variant="text" href="/" sx={{ mt: 1, color: '#666' }}>
                ← Back to Sign In
              </Button>
            </>
          ) : (
            <Button fullWidth variant="contained" href="/"
              sx={{ background: '#2E7D32', py: 1.5, borderRadius: 2 }}>
              Go to Sign In →
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
}
